import useCustomMutation from "@/composables/useCustomMutation";
import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import type { Department } from "@/interfaces/department";
import { MutationParams } from "@/interfaces/mutationParams";
import type { PaginatedResponse } from "@/interfaces/paginatedResponse";
import axios from "@/lib/axios";
import { useCompanyDepartmentStore } from "@/stores/companyDepartments";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { get } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { type MaybeRef, computed, watch } from "vue";
import { useToast } from "vue-toastification";

interface Props {
  params: Params;
  immediate?: boolean;
  // Filtros server-side: search hace LIKE en name; page/perPage paginación
  // estándar Laravel (per_page default 10 en backend).
  search?: MaybeRef<string>;
  page?: MaybeRef<number>;
  perPage?: number;
}

interface Params extends Omit<Partial<Department>, "id"> {
  id?: string | number;
  includes?: string[];
}

interface ListParams {
  companyId: string | number;
  search?: string;
  page?: number;
  perPage?: number;
}

interface CreateParams {
  name: string;
  companyId: string | number;
}

const getDepartments = async (
  params: ListParams,
): Promise<PaginatedResponse<Department>> => {
  const { data } = await axios.get<PaginatedResponse<Department>>(
    `api/companies/${params.companyId}/departments`,
    {
      params: {
        search: params.search || undefined,
        page: params.page,
        per_page: params.perPage,
      },
      errorHandling: false,
    } as CustomAxiosRequestConfig,
  );
  return data;
};

const createDepartment = async (params: CreateParams) => {
  const { data } = await axios.post<Department>(
    `api/companies/${params.companyId}/departments`,
    params,
  );
  return data;
};

// El backend SIEMPRE pagina (default 10). Los consumidores legacy (dropdowns
// de UserForm, UsersTab, etc.) esperan la lista completa: sin perPage
// explícito se pide 100 para no truncarlos a la primera página.
const LEGACY_PER_PAGE = 100;

const useCompanyDepartment = (props?: Props) => {
  const { params, immediate = true } = props || {};
  const { setCompanyId, setDepartments } = useCompanyDepartmentStore();
  const { companyId, departments } = storeToRefs(useCompanyDepartmentStore());
  const toast = useToast();

  const search = computed(() => get(props?.search) ?? "");
  const page = computed(() => get(props?.page) ?? 1);
  const perPage = props?.perPage ?? LEGACY_PER_PAGE;

  const { data, isLoading, refetch, isFetching } = useQuery(
    ["companyDepartments", params?.companyId, search, page, perPage],
    () =>
      getDepartments({
        companyId: companyId.value! || params?.companyId!,
        search: search.value,
        page: page.value,
        perPage,
      }),
    {
      enabled: params ? immediate : false,
      retry: false,
      // Mantiene la página anterior visible mientras carga la nueva:
      // sin saltos de layout al paginar o buscar.
      keepPreviousData: true,
    },
  );

  const meta = computed(() => data.value?.meta ?? null);

  watch(data, (value) => {
    if (value) {
      setDepartments(value.data);
    }
  });

  const createMutation = useMutation(
    (mutationParams: MutationParams<CreateParams>) =>
      createDepartment(mutationParams.params),
    useCustomMutation(toast, "create"),
  );

  return {
    departments,
    meta,
    isFetching,
    isLoading,

    refetch: () => {
      if (params) {
        refetch();
      }
    },

    setCompanyId,

    createDepartmentMutation: createMutation,
    createDepartment: createMutation.mutate,
    createDepartmentAsync: createMutation.mutateAsync,
    createDepartmentLoading: createMutation.isLoading,
  };
};

export default useCompanyDepartment;
